import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { CLAIM_TYPES } from "../../../api/constants";
import ToastUtil from "../../../components/ToastNotification/ToastUtil";
import { config } from "../../../config/index";
import { newFetch, POST } from "../../../helpers/fetch";
import Upload from "../../Input/Upload";

const { SERVER_URL } = config;

const ClaimsUpload = (props) => {
  const { t } = useTranslation();
  const Status = "Pending";
  const [Amount, setAmount] = useState(0);
  const [Remarks, StatussetRemarks] = useState("");
  const [dateReceipt, setDateReceipt] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [files, setFiles] = useState([]);
  const [filteredClaimTypes, setFilteredClaimTypes] = useState([]);
  const history = useHistory();
  const asscoiateId = JSON.parse(localStorage.getItem("user")).id;

  useEffect(() => {
    if (!props.activeSite?.enable_ss_claim_limit) {
      setFilteredClaimTypes(
        props.claimTypes.filter((item) => item.id !== CLAIM_TYPES.SAFETY_SHOES)
      );
    } else {
      setFilteredClaimTypes(props.claimTypes);
    }
  }, [props.claimTypes, props.activeSite]);

  const handleSubmit = async () => {
    // Basic frontend validation before making API call
    if (!dateReceipt) {
      ToastUtil.error(`${t("Select date of receipt")}`);
      return;
    }

    if (!receiptNo.trim()) {
      ToastUtil.error(`${t("Enter receipt no.")}`);
      return;
    }

    if (!props.selectedClaimType) {
      ToastUtil.error(`${t("Select claim type")}`);
      return;
    }

    if (!Amount || Amount <= 0) {
      ToastUtil.error(`${t("Enter valid amount")}`);
      return;
    }

    if (props.activeSite?.enable_claim_receipt) {
      if (props.activeSiteAssociate?.claim_no_receipt) {
        if (
          props.claimNoOfReceipts >= props.activeSiteAssociate.claim_no_receipt
        ) {
          ToastUtil.error(
            `${t("Raise claims exceeded, Maximum is")} ${
              props.activeSiteAssociate.claim_no_receipt
            }.`
          );
          return;
        }
      }
    }

    if (files.length === 0) {
      ToastUtil.error(`${t("Please upload your purchase receipt or invoice")}`);
      return;
    }

    if (
      props.activeSite?.enable_ss_claim_limit &&
      parseInt(props.selectedClaimType) === CLAIM_TYPES.SAFETY_SHOES
    ) {
      const claimsDocumentSlots = claimDocumentSlots();

      let isValidSafetyShoesFiles = true;

      for (let slot of claimsDocumentSlots) {
        const slotFile = files.find((item) => item.uploadSlot === slot.key);

        if (!slotFile) {
          isValidSafetyShoesFiles = false;
          ToastUtil.error(t(`Please upload ${slot.description}`));
        }
      }

      if (!isValidSafetyShoesFiles) {
        return;
      }
    }

    // Prepare form data for API
    let formData = new FormData();
    formData.append("associate_id", asscoiateId);
    formData.append("type_id", props.selectedClaimType);
    formData.append("date_of_receipt", dateReceipt);
    formData.append("amount", Amount);
    formData.append("receipt_no", receiptNo);
    formData.append("remarks", Remarks);
    formData.append("status", Status);
    formData.append("site_id", props?.activeSite?.id || "");

    for (let file of files) {
      formData.append("files", file);
    }

    if (props.activeSite?.enable_claim_percentage) {
      if (
        props.activeSiteAssociate?.claim_percentage &&
        parseInt(props.selectedClaimType) === CLAIM_TYPES.STAFF_PURCHASE
      ) {
        formData.append(
          "deduct_percent",
          props.activeSiteAssociate.claim_percentage
        );
      }
    }

    let deductAmount = 0;
    if (props.activeSite?.enable_claim_limit) {
      if (props.activeSite?.enable_claim_percentage) {
        if (props.activeSiteAssociate?.claim_percentage) {
          deductAmount =
            Amount *
            parseFloat(props.activeSiteAssociate.claim_percentage / 100);
        } else {
          deductAmount = Amount;
        }
      } else {
        deductAmount = Amount;
      }

      if (Number(props.claimBalance) - Number(deductAmount) < 0) {
        deductAmount = props.claimBalance;
      }
    } else {
      if (props.activeSite?.enable_claim_percentage) {
        if (props.activeSiteAssociate?.claim_percentage) {
          deductAmount =
            Amount *
            parseFloat(props.activeSiteAssociate.claim_percentage / 100);
        } else {
          deductAmount = Amount;
        }
      } else {
        deductAmount = Amount;
      }
    }

    if (
      parseInt(props.selectedClaimType) === CLAIM_TYPES.TRANSPORTATION ||
      parseInt(props.selectedClaimType) === CLAIM_TYPES.SAFETY_SHOES
    ) {
      deductAmount = Amount;
    }

    formData.append("deduct_amount", deductAmount);
    formData.append(
      "claim_settings",
      JSON.stringify({
        enable_claim_limit: props.activeSite?.enable_claim_limit,
        enable_ss_claim_limit: props.activeSite?.enable_ss_claim_limit,
        enable_claim_percentage: props.activeSite?.enable_claim_percentage,
        claim_limit: props.activeSiteAssociate?.claim_limit || null,
        ss_claim_limit: props.activeSiteAssociate?.ss_claim_limit || null,
      })
    );

    // API call
    let api_path = `/claims`;
    let response = await newFetch(POST, api_path, formData, "multi");

    if (response.success) {
      ToastUtil.success(
        response.message || `${t("Claim submitted successfully")}`
      );

      // Clear form after success
      setAmount(0);
      StatussetRemarks("");
      props.setSelectedClaimType("");
      setFiles([]);
      props.updateState(); // Refresh data if needed

      history.push("/claims");
    } else {
      ToastUtil.error(response.message || `${t("Failed to submit claim")}`);
    }
  };

  const handleAmountChange = (value) => {
    setAmount(value);
  };

  const handleDateReceiptChange = (value) => {
    setDateReceipt(value);
    const date = new Date(value);
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    if (!isNaN(month)) {
      props.setClaimMonth(month);
    }
    if (!isNaN(year)) {
      props.setClaimYear(year);
    }
    props.updateState();
  };

  const handleClaimTypeSelection = (value) => {
    //clear the files everytime you change claim type.
    setFiles([]);

    props.setSelectedClaimType(value);
  };

  const claimDocumentSlots = () => {
    if (
      props.activeSite?.enable_ss_claim_limit &&
      parseInt(props.selectedClaimType) === CLAIM_TYPES.SAFETY_SHOES
    ) {
      return [
        {
          key: "receipt-invoice",
          title: "Receipt / Invoice",
          description: "Your purchase receipt or invoice",
        },
        {
          key: "safety-shoes-photo",
          title: "Photo of you wearing the safety shoes",
          description: "A clear photo showing you wearing the shoes",
        },
        {
          key: "safety-shoes-certification",
          title: "Certification of safety shoes",
          description: "Safety certification or compliance document",
        },
      ];
    } else {
      return [
        {
          key: "receipt-invoice",
          title: "Receipt / Invoice",
          description: "Upload your purchase receipt or invoice",
        },
      ];
    }
  };

  return (
    <>
      {props.claimData ? (
        <Card>
          <CardContent>
            <div className="edit-claim-wrapper">
              {/* Date of Receipt */}
              <div className="form-group">
                <div className="header-thick">{t("Date of Receipt")}</div>
                <div className="edit-claim-values">
                  {props.claimData.date_of_receipt ?? "-"}
                </div>
              </div>

              {/* Receipt No. */}
              <div className="form-group">
                <div className="header-thick">{t("Receipt No.")}</div>
                <div className="edit-claim-values">
                  {props.claimData.receipt_no ?? "-"}
                </div>
              </div>

              {/* Claim Type */}
              <div className="form-group">
                <div className="header-thick">{t("Type")}</div>
                <div className="edit-claim-values">
                  {props.claimData.claim_type_name || "-"}
                </div>
              </div>

              {/* Amount */}
              <div className="form-group">
                <div className="header-thick">{t("Amount")}</div>
                <div className="edit-claim-values">
                  {props.claimData.amount != null
                    ? props.claimData.amount.toFixed(2)
                    : "0.00"}
                </div>
              </div>

              {/* File Upload */}
              <div className="form-group py-2" style={{ flexWrap: "wrap" }}>
                <label className="header-thick">{t("Uploaded Files")}</label>
                <br></br>
                <p>
                  {props.claimData.orig_file ? (
                    <a
                      href={`${SERVER_URL}/storage/claims/${props.claimData.new_file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="edit-claim-values"
                    >
                      {props.claimData.orig_file}
                    </a>
                  ) : (
                    <div className="edit-claim-values">-</div>
                  )}
                </p>
                <p>
                  {props.claimData?.orig_file_2 ? (
                    <a
                      href={`${SERVER_URL}/storage/claims/${props.claimData.new_file_2}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="edit-claim-values"
                    >
                      {props.claimData.orig_file_2}
                    </a>
                  ) : (
                    <div className="edit-claim-values">-</div>
                  )}
                </p>
                <p>
                  {props.claimData?.orig_file_3 ? (
                    <a
                      href={`${SERVER_URL}/storage/claims/${props.claimData.new_file_3}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="edit-claim-values"
                    >
                      {props.claimData.orig_file_3}
                    </a>
                  ) : (
                    <div className="edit-claim-values">-</div>
                  )}
                </p>
              </div>

              {/* Remarks */}
              <div className="form-group">
                <div className="header-thick">{t("Remarks")}</div>
                <div className="edit-claim-values">
                  {props.claimData.remarks || "-"}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <div className="edit-claim-wrapper">
              <div className="form-group">
                <div className="header-thick">{t("Date of Receipt")}</div>
                <input
                  className="form-control"
                  type="date"
                  value={dateReceipt}
                  onChange={(e) => handleDateReceiptChange(e.target.value)}
                />
              </div>

              <div className="form-group">
                <div className="header-thick">{t("Receipt No.")}</div>
                <input
                  className="form-control"
                  type="text"
                  value={receiptNo}
                  placeholder={t("Enter receipt no.")}
                  maxLength={100}
                  onChange={(e) => setReceiptNo(e.target.value)}
                />
              </div>

              <div className="form-group">
                <div className="header-thick">{t("Type")}</div>
                <select
                  name="applyType"
                  id="applyType"
                  value={props.selectedClaimType || ""}
                  className="form-select  edit-claim-values"
                  onChange={(e) => handleClaimTypeSelection(e.target.value)}
                >
                  <option value="">{t("Select a claim type")}</option>
                  {filteredClaimTypes.map((type) => (
                    <option value={type.id} key={type.id}>
                      {t(type.name)}
                    </option>
                  ))}
                </select>

                {props.activeSite?.enable_claim_limit &&
                  props.claimMonth &&
                  parseInt(props.selectedClaimType) ===
                    CLAIM_TYPES.STAFF_PURCHASE && (
                    <div className="my-2 mx-1">
                      <p className="claim-style ps-4">
                        {t("Claim Balance")} (
                        {new Date(0, props.claimMonth - 1).toLocaleString(
                          "default",
                          {
                            month: "long",
                          }
                        )}{" "}
                        {props.claimYear}): {props.claimBalance} SGD
                      </p>
                    </div>
                  )}

                {props.activeSite?.enable_ss_claim_limit &&
                  parseInt(props.selectedClaimType) ===
                    CLAIM_TYPES.SAFETY_SHOES && (
                    <div className="my-2 mx-1">
                      <p className="claim-style ps-4">
                        {t("Claim Balance")} (
                        {props?.claimYear ||
                          new Date().toLocaleString("default", {
                            year: "numeric",
                          })}
                        ): {props.safetyShoesClaimBalance} SGD
                      </p>
                    </div>
                  )}
              </div>

              <div className="form-group">
                <div className="header-thick">{t("Amount")}</div>
                <input
                  className="form-control"
                  type="number"
                  value={Amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                />
              </div>

              <div className="form-group">
                <Upload
                  files={files}
                  setFiles={setFiles}
                  accept={"image/jpg, image/jpeg, image/png, application/pdf"}
                  variant="claim-documents"
                  claimDocumentSlots={claimDocumentSlots()}
                />
              </div>

              <div className="form-group">
                <div className="header-thick">{t("Remarks")}</div>
                <textarea
                  className="form-control"
                  rows="5"
                  style={{ resize: "none" }}
                  placeholder="Remarks"
                  id="Remarks"
                  name="Remarks"
                  value={Remarks}
                  onChange={({ target: { value } }) => StatussetRemarks(value)}
                />
              </div>

              <div className="edit-button-wrapper">
                <button
                  onClick={() => handleSubmit()}
                  className="btn btn-design btn-brand"
                >
                  {t("Save")}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ClaimsUpload;
